/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { useAuth } from "@slr/shared-library";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "configs";
import { useSolution1UsersApi } from "services/solution1/useSolution1UsersApi";
import { useUsersApi } from "services/user/useUsersApi";

export const useGetCurrentUserProfileWithSolution1 = () => {
  const { isAuthenticated } = useAuth();

  const { getCurrentUserProfile: getUserProfileFromUserService } =
    useUsersApi();
  const { getCurrentUserProfile: getUserProfileFromSolution1 } =
    useSolution1UsersApi();

  return useQuery({
    queryKey: [queryKeys.getCurrentUserProfile, "withSolution1"],
    queryFn: async () => {
      try {
        const [userServiceProfile, solution1Profile] = await Promise.allSettled(
          [getUserProfileFromUserService(), getUserProfileFromSolution1()]
        );

        const userProfile =
          userServiceProfile.status === "fulfilled"
            ? userServiceProfile.value
            : null;

        const solution1Data =
          solution1Profile.status === "fulfilled"
            ? solution1Profile.value
            : null;

        const result = {
          ...userProfile,
          billingAddress: solution1Data?.billingAddress,
        };

        return result;
      } catch (error) {
        console.error("Error loading profiles:", error);
        throw error;
      }
    },
    enabled: isAuthenticated,
  });
};
